import { FC, useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useAppMobile } from '@common/selectors/app.selectors';
import { pageScrollTo } from '@common/methods/pageScrollTo';
import { isTrueOrZero } from '@common/methods/isTrueOrZero';
import { usePrevious } from '../../../hooks/render/usePrevious';
import {
	SlideDownBoxStyle,
	SlideDownContentStyle,
	SlideDownTitleStyle,
} from './slideDownBox.style';

export const SlideDownBox: FC<{
	Title?: FC<{ active: boolean }>;
	Content?: FC;
	isColor?: boolean;
	titleClasses?: string;
	contentClasses?: string;
	isContentInitVisible?: boolean;
	isContentVisible?: boolean;
	callback?: (isVisible: boolean) => void;
	noScrollTo?: boolean;
	boxClasses?: string;
	noDashboard?: boolean;
	name: string;
}> = ({
	Title,
	Content,
	isColor,
	titleClasses,
	contentClasses,
	isContentInitVisible,
	isContentVisible,
	callback,
	boxClasses,
	noDashboard,
	name,
	noScrollTo,
}) => {
	const [_isVisible, setIsVisible] = useState<boolean>(
		isTrueOrZero(isContentInitVisible) ? isContentInitVisible : false,
	);
	const ref = useRef<HTMLDivElement>(null);
	const timeoutRef = useRef(null);
	const prevIsContentVisible = usePrevious(isContentVisible);
	const isMobile = useAppMobile();

	const handleClickTitle = useCallback(
		(event?: MouseEvent, isVisible?: boolean) => {
			if (isVisible === undefined) {
				isVisible = !_isVisible;
			}
			if (callback) {
				callback(isVisible);
			}
			setIsVisible(isVisible);
			if (isVisible && isMobile && ref?.current && !noScrollTo) {
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}
				timeoutRef.current = setTimeout(() => {
					pageScrollTo({ top: ref?.current?.offsetTop - 50 });
				}, 300);
			}
		},
		[_isVisible, callback, isMobile, noScrollTo],
	);

	useEffect(() => {
		if (isContentVisible !== prevIsContentVisible) {
			handleClickTitle(undefined, isContentVisible);
		}
	}, [isContentVisible, handleClickTitle, prevIsContentVisible]);

	return (
		<SlideDownBoxStyle
			ref={ref}
			className={boxClasses}
			noDashboard={noDashboard}
		>
			<SlideDownTitleStyle
				name={`slide-down-title-${name}`}
				onClick={() => handleClickTitle()}
				isColor={isColor}
				className={classnames('icon-wrapper', titleClasses)}
			>
				{Title && <Title active={_isVisible} />}
				{Content && (
					<i
						className={classnames('icon__arrow-down', {
							opened: _isVisible,
						})}
					/>
				)}
			</SlideDownTitleStyle>
			{Content && (
				<SlideDownContentStyle isActive={_isVisible} className={contentClasses}>
					{(_isVisible || !process.browser) && <Content />}
				</SlideDownContentStyle>
			)}
		</SlideDownBoxStyle>
	);
};
